import React, { useEffect, useState } from 'react';
import { pick } from 'lodash';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import dataActions from 'core/data/actions';
import groupActions from 'core/groups/actions';
import { withStyles } from 'theme/withStyles';
import RawHtml from 'common/RawHtml';
import hasError from 'utils/hasError';

const styles = ({ spacing, typography }) => ({
  button: {
    '& button': {
      border: `1px solid black`,
      boxShadow: 'none',
      height: 40,
      width: '100% !important',
    },
    margin: 0,
    textAlign: 'center',
  },
  buttonWrap: {
    '& small': {
      ...typography.p,
      ...typography.p3,
    },
    margin: [spacing.sm, 0],
    width: '100%',
  },
  disabled: {
    opacity: '50%',
    pointerEvents: 'none',
  },
});

export const GooglePayBare = ({
  changeData,
  classes,
  config,
  disabled,
  groupData,
  groupName,
  nextGroup,
  options,
  total,
}) => {
  const [scriptLoaded, setScriptLoaded] = useState(false);

  let paymentsClient;

  const {
    currencyCode = 'USD',
    merchantDisplayName = 'Example Merchant',
    supportedNetworks = ['AMEX', 'DISCOVER', 'JCB', 'MASTERCARD', 'VISA'],
    allowedAuthMethods = ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
    apiVersion = 2,
    apiVersionMinor = 0,
    gateway = 'example',
    gatewayMerchantId = 'exampleGatewayMerchantId',
    tokenizationType = 'PAYMENT_GATEWAY',
    totalPriceStatus = 'FINAL',
    environment = 'TEST',
    merchantId = '',
  } = config;

  const allowedPaymentMethodsType = 'CARD';

  const structuredConfig = {
    cardPaymentMethod: {
      parameters: {
        allowedAuthMethods,
        allowedCardNetworks: supportedNetworks,
      },
      tokenizationSpecification: {
        parameters: {
          gateway,
          gatewayMerchantId,
        },
        type: tokenizationType,
      },
      type: allowedPaymentMethodsType,
    },
    environment,
    paymentDataRequest: {
      allowedPaymentMethods: [
        {
          parameters: {
            allowedAuthMethods,
            allowedCardNetworks: supportedNetworks.map((n) => n.toUpperCase()),
          },
          tokenizationSpecification: {
            parameters: {
              gateway,
              gatewayMerchantId,
            },
            type: tokenizationType,
          },
          type: allowedPaymentMethodsType,
        },
      ],
      apiVersion: Number(apiVersion),
      apiVersionMinor: Number(apiVersionMinor),
      merchantInfo: {
        merchantId,
        merchantName: merchantDisplayName,
      },
      transactionInfo: {
        currencyCode,
        totalPrice: total,
        totalPriceStatus,
      },
    },
  };

  function setGooglePaymentsClient() {
    if (!paymentsClient) {
      paymentsClient = new window.google.payments.api.PaymentsClient({
        environment: structuredConfig.environment,
      });
    }
  }

  function processPayment(paymentData) {
    const {
      paymentMethodData: {
        tokenizationData: { token },
      },
    } = paymentData;

    changeData('appRequest_mobileWallet_encryptedPayment', btoa(token));
    changeData('appRequest_mobileWalletMethod', 'GOOGLE_PAY');
    changeData('appRequest_fundingMethod', 'MOBILE_WALLET');

    nextGroup(groupName, {
      ...groupData,
      appRequest_fundingMethod: 'MOBILE_WALLET',
      appRequest_mobileWalletMethod: 'GOOGLE_PAY',
      appRequest_mobileWallet_encryptedPayment: btoa(token),
    });
  }

  function onGooglePaymentButtonClicked() {
    const { paymentDataRequest } = structuredConfig;

    paymentsClient
      .loadPaymentData(paymentDataRequest)
      .then((paymentData) => processPayment(paymentData))
      .catch(() => {});
  }

  function addGooglePayButton() {
    const button = paymentsClient.createButton({
      buttonColor: 'black',
      buttonType: 'short',
      onClick: onGooglePaymentButtonClicked,
    });

    document.querySelector('.google-pay-button').innerHTML = '';
    document.querySelector('.google-pay-button').appendChild(button);
  }

  function onGooglePayLoaded() {
    setGooglePaymentsClient();
    paymentsClient
      .isReadyToPay(structuredConfig.paymentDataRequest)
      .then((response) => {
        if (response.result) {
          addGooglePayButton();
        }

        setScriptLoaded(true);
      })
      .catch(() => {});
  }

  useEffect(() => {
    if (!scriptLoaded && !window.google?.payments?.api?.PaymentsClient) {
      if (!document.querySelector('#google-pay')) {
        const script = document.createElement('script');

        script.src = 'https://pay.google.com/gp/p/js/pay.js';
        script.async = true;
        script.onload = onGooglePayLoaded;
        script.id = 'google-pay';

        document.body.appendChild(script);
      }
    } else {
      onGooglePayLoaded();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div
        className={classnames('region-google-pay', classes.buttonWrap, {
          [classes.disabled]: disabled,
          'disable-mobile-wallet': disabled,
        })}
        style={{ opacity: disabled ? '50%' : '100%', pointerEvents: disabled ? 'none' : 'all' }}
      >
        <div className={classnames('google-pay-button', classes.button)} />
        {}
        {options.labelDescr && <RawHtml type="small">{options.labelDescr}</RawHtml>}
      </div>
    </>
  );
};

GooglePayBare.propTypes = {
  changeData: PropTypes.func.isRequired,
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  config: PropTypes.shape({
    allowedAuthMethods: PropTypes.arrayOf(PropTypes.string),
    allowedPaymentMethods: PropTypes.arrayOf(PropTypes.string),
    apiVersion: PropTypes.string,
    apiVersionMinor: PropTypes.string,
    currencyCode: PropTypes.string,
    environment: PropTypes.string,
    gateway: PropTypes.string,
    gatewayMerchantId: PropTypes.string,
    merchantDisplayName: PropTypes.string,
    merchantId: PropTypes.string,
    supportedNetworks: PropTypes.arrayOf(PropTypes.string),
    tokenizationType: PropTypes.string,
    totalPriceStatus: PropTypes.string,
  }).isRequired,
  disabled: PropTypes.bool,
  groupData: PropTypes.shape({}).isRequired,
  groupName: PropTypes.string.isRequired,
  nextGroup: PropTypes.func.isRequired,
  options: PropTypes.shape({
    labelDescr: PropTypes.string,
  }).isRequired,
  total: PropTypes.string,
};

GooglePayBare.defaultProps = {
  disabled: true,
  total: String(0),
};

const mapStateToProps = ({ configs, data, fields: stateFields, groups, ui }) => {
  const {
    FUNDING_AMOUNT: { fields },
  } = groups;
  const group = groups[ui.activeGroup] || {};

  return {
    config: {
      ...configs.mobileWallet,
      ...configs.googlePay,
    },
    disabled: hasError(pick(stateFields, group.fields)) || !data.appRequest_totalFundingAmount,
    groupData:
      fields &&
      fields.reduce(
        (groupData, field) => ({
          ...groupData,
          [field]: data[field],
        }),
        {},
      ),
    groupName: groups.FUNDING_AMOUNT.name,
    total: String(data.appRequest_totalFundingAmount),
  };
};

const mapDispatchToProps = {
  changeData: dataActions.changeData,
  nextGroup: groupActions.next,
};

const GooglePayStyled = withStyles(styles)(GooglePayBare);

export default connect(mapStateToProps, mapDispatchToProps)(GooglePayStyled);
